import { Fragment } from "react";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@buildpy/ui/table";
import { EMPTY_MARK, formatQty } from "@buildpy/core/lib/format";
import Money from "@/components/money";
import { cn } from "@/lib/utils";
import {
  statementQtyText,
  statementSummaryRows,
  statementTagOf,
  type CurrentStatement,
  type StatementAudience,
  type StatementDelta,
} from "@buildpy/core/modules/change-order/current-statement";

// SectionCard 안 촘촘한 표 — 계약 상세와 같은 자(공통 Table 기본 h-12·p-4 를 줄인다).
const HEAD = "h-auto py-1 pl-0 pr-2 text-xs font-normal";
const CELL = "py-1.5 pl-0 pr-2";
const NUM = `${CELL} text-right tabular-nums whitespace-nowrap`;

const signedQty = (qty: number): string => `${qty > 0 ? "+" : qty < 0 ? "-" : ""}${formatQty(Math.abs(qty))}`;

const DeltaCell = ({ delta }: { delta: StatementDelta | null }) => (
  <TableCell className={NUM}>
    {delta ? (
      <span className="flex flex-col items-end">
        <Money className={cn(delta.amount < 0 && "text-destructive")} signed value={delta.amount} />
        <span className="text-xs text-muted-foreground">{signedQty(delta.qty)}</span>
      </span>
    ) : (
      <span className="text-muted-foreground">{EMPTY_MARK}</span>
    )}
  </TableCell>
);

const AmountCell = ({ amount, qty, strong }: { amount: number; qty: string | null; strong?: boolean }) => (
  <TableCell className={cn(NUM, strong && "font-medium")}>
    <span className="flex flex-col items-end">
      <Money value={amount} />
      {qty !== null && <span className="text-xs text-muted-foreground">{qty}</span>}
    </span>
  </TableCell>
);

// 현재 내역서 표 — 원 계약 · 차수별 증감 · 현재를 한 줄에 둔다(합계 줄은 statementSummaryRows 한 벌).
const CurrentStatementTable = ({ statement, audience = "staff" }: { statement: CurrentStatement; audience?: StatementAudience }) => {
  const width = 4 + statement.columns.length;
  return (
    <Table className="min-w-[36rem]" data-testid="current-statement-table">
      <TableHeader>
        <TableRow>
          <TableHead className={HEAD}>항목</TableHead>
          <TableHead className={HEAD}>단위</TableHead>
          <TableHead className={`${HEAD} text-right`}>원 계약</TableHead>
          {statement.columns.map((c) => (
            <TableHead key={c.id} className={`${HEAD} text-right`} title={c.title}>
              {c.seq}차 변경
            </TableHead>
          ))}
          <TableHead className={`${HEAD} text-right`}>현재</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {statement.groups.map((group) => (
          <Fragment key={group.key}>
            <TableRow variant="group">
              <TableCell className={`${CELL} pl-1 font-semibold`} colSpan={width}>
                {group.label}
              </TableCell>
            </TableRow>
            {group.lines.map((line) => {
              const tag = statementTagOf(line, statement.grouping, group.kind);
              return (
                <TableRow key={line.key}>
                  <TableCell className={CELL}>
                    <span className="flex flex-col">
                      <span>{line.label}</span>
                      {tag && <span className="text-xs text-muted-foreground">{tag}</span>}
                    </span>
                  </TableCell>
                  <TableCell className={`${CELL} whitespace-nowrap`}>{line.unit}</TableCell>
                  <AmountCell amount={line.original_amount} qty={formatQty(line.original_qty)} />
                  {line.deltas.map((d, i) => (
                    <DeltaCell key={statement.columns[i].id} delta={d} />
                  ))}
                  <AmountCell amount={line.current_amount} qty={statementQtyText(line, audience)} strong />
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell className={`${CELL} text-muted-foreground`} colSpan={2}>
                {group.label} 소계
              </TableCell>
              <TableCell className={NUM}><Money value={group.original} /></TableCell>
              {group.deltas.map((d, i) => (
                <TableCell key={statement.columns[i].id} className={NUM}>
                  {d === 0 ? EMPTY_MARK : <Money signed value={d} />}
                </TableCell>
              ))}
              <TableCell className={`${NUM} font-medium`}><Money value={group.current} /></TableCell>
            </TableRow>
          </Fragment>
        ))}
        {statementSummaryRows(statement, audience).map((row) => (
          <TableRow key={row.key} variant={row.key === "total" ? "total" : "default"}>
            <TableCell className={`${CELL} pl-1`} colSpan={2}>
              <span className="flex flex-col">
                <span>{row.label}</span>
                {row.note && <span className="text-xs font-normal text-muted-foreground">{row.note}</span>}
              </span>
            </TableCell>
            <TableCell className={NUM}>{row.original === null ? EMPTY_MARK : <Money value={row.original} />}</TableCell>
            {row.deltas.map((d, i) => (
              <TableCell key={statement.columns[i].id} className={NUM}>
                {d === null ? EMPTY_MARK : <Money signed value={d} />}
              </TableCell>
            ))}
            <TableCell className={NUM} data-testid={row.key === "total" ? "current-statement-total" : undefined}>
              <Money value={row.current} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default CurrentStatementTable;
